import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import styled from 'styled-components';
import { operationType } from "../../../../../services/types";
import { updateNodeValue } from "../../../../../services/method";
import { Textarea } from "../../../../form/textarea";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";

type TextToSpeechProps = NodeProps<operationType> & {
    setChildMinimize?: (fn: (id: string) => void) => void;
    setChildSettings?: (fn: (id: string) => void) => void;
};

export function TextToSpeech({ id, data, setChildMinimize, setChildSettings }: TextToSpeechProps) {
    const { setNodes } = useReactFlow();
    const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
    const [isExpanded, setIsExpanded] = React.useState(false);

    // --
    React.useEffect(() => {
        if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
        if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setChildMinimize, setChildSettings]);

    // Initialize default values
    React.useEffect(() => {
        if (!data.value) {
            setNodes((nds) => nds.map((node) => {
                if (node.id === id) return { ...node, data: { ...node.data, value: { text: '' } } };
                else return node;
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---
    const handleChangeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        updateNodeValue(id, setNodes, { text: e.target.value });
    }

    const handleToggleStreamMode = () => {
        updateNodeValue(id, setNodes, { streamMode: !data.value?.streamMode });
    }

    // ---
    const isWarning = false;

    return (<>
        <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
        <Main>
            {isWarning ? <>
                <div id="warning"></div>
                <span id="warningIcon"><IoWarningOutline size={21} /></span>
            </> : data.output ? <>
                <div id="ran"></div>
                <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
            </> : null}
            {isExpanded ? <ExpandedBlock
                data={{ id, ...data }}
                handleClose={() => setIsExpanded(false)}
                isWarning={isWarning}
                style={{ minWidth: '380px' }}
            ><Styled>
                    <Textarea
                        value={data.value?.text}
                        placeholder="Enter text to convert to speech..."
                        handleChange={handleChangeText}
                    />
                    <div className="streamToggle">
                        <label>
                            <input
                                type="checkbox"
                                checked={data.value?.streamMode || false}
                                onChange={handleToggleStreamMode}
                            />
                            <span>Streaming Mode</span>
                        </label>
                        <span className="hint">Play audio directly without waiting for upload</span>
                    </div>
                    {/* Audio Preview */}
                    {data.output && (
                        <div className="audioPreview">
                            <div className="title">Audio Output</div>
                            <audio controls src={data.output}>
                                Your browser does not support the audio element.
                            </audio>
                        </div>
                    )}
                </Styled></ExpandedBlock> : null}
        </Main>
    </>);
}

// ---
const Main = styled.div`
   min-width: 150px;
   padding: 0;
`;

const Styled = styled.div`
   .textarea { 
      width: 100%;
   }
   .info {
      font-size: 12px;
      color: #888;
      margin-top: 8px;
   }
   .title {
      font-size: 13px;
      font-weight: 600;
      margin-top: 12px;
      margin-bottom: 6px;
   }
   .audioPreview {
      margin-top: 16px;
      audio {
         width: 100%;
         margin-top: 8px;
      }
   }
   .streamToggle {
      margin-top: 12px;
      label {
         display: flex;
         align-items: center;
         gap: 8px;
         font-size: 13px;
         font-weight: 500;
         cursor: pointer;
         input {
            cursor: pointer;
            width: 16px;
            height: 16px;
         }
      }
      .hint {
         display: block;
         font-size: 11px;
         color: #888;
         margin-top: 4px;
         margin-left: 24px;
      }
   }
`;